import React from 'react';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { useWindowSizeInfo } from '$/utils/useWindowSize';
import Button from '../Button';
import { NextIcon, PrevIcon } from '$/images/FontAwesomeIcons';
import { useThemeContext } from '../ThemeContext';
import { useWizard } from './WizardFlow';
import { useFormContext } from 'react-hook-form';
import Progress, { ProgressProps } from 'semantic-ui-react/dist/commonjs/modules/Progress';
import Form from '../Form';
import { withCustomHookValue } from '$/hooks/withPartialContexts';
import { SubmitButtonProps } from '../Button/Submit';
import { useRefCallback } from '$/hooks/useRefCallback';

interface BasicProps {
  className?: string;
  rowClassName?: string;
  errorClassName?: string;
  children?: React.ReactNode;
}

export const WizardAreaWidthRem = 50;

type ButtonRowProps = BasicProps & {
  backButtonProps?: BasicProps;
};

type WizardAreaProps = BasicProps & { after?: React.ReactNode };

interface WizardComponents {
  Area: React.FC<WizardAreaProps>;
  Container: React.FC<BasicProps & { render: boolean | undefined }>;
  Title: React.FC<BasicProps>;
  Subtitle: React.FC<BasicProps>;
  Text: React.FC<BasicProps>;
  ProgressBar: React.FC<BasicProps & ProgressProps & { startPercent?: number }>;
  BackButton: React.FC<BasicProps & { onClick?: () => Promise<void> }>;
  Link: React.FC<BasicProps>;
  Separator: React.FC<BasicProps>;
  NextButton: React.FC<
    BasicProps & { type?: 'button' | 'submit'; onClick?: () => void; disabled?: boolean }
  >;
  ErrorArea: React.FC<BasicProps>;
  ButtonRow: React.FC<ButtonRowProps>;
}

const ProgressBar: React.FC<
  BasicProps &
    ProgressProps & {
      startPercent?: number;
    }
> = ({ className, children: _children, startPercent = 0, ...props }) => {
  const wizard = useWizard();
  const percent =
    startPercent + (wizard.activeStep / (wizard.stepCount - 1)) * (100 - startPercent);
  return (
    <div
      className={`wizard-bar mx-auto ${className}`}
      css={[
        tw`absolute bottom-0 left-0 right-0`,
        css`
          .ui.progress {
            margin: 0 0.5rem 1rem !important;
            background-color: #c3dadf;
            ${tw`rounded-full overflow-hidden`}

            .bar {
              background-color: #39a1b2;
              ${tw`rounded-full rounded-r-none`}
            }
          }
        `,
      ]}
    >
      <Progress size='small' percent={percent} indicating={false} active={false} {...props} />
    </div>
  );
};

type AreaCustomHookValue = {
  isMd: boolean;
  inModal: boolean;
};
const Area = withCustomHookValue<WizardAreaProps & AreaCustomHookValue, AreaCustomHookValue>(() => {
  const { md: isMd } = useWindowSizeInfo();
  const { inModal } = useWizard();
  return {
    isMd,
    inModal,
  };
}, 'Area')(({ children, className, inModal, isMd, after }) => {
  return (
    <div
      className={`warea-wrap flex relative w-full flex-col mx-auto overflow-y-scroll ${inModal ? 'in-modal' : 'no-modal'}`}
      css={css`
        &.in-modal .warea-width {
          width: 100%;
          padding-bottom: 4rem;
        }

        &.no-modal .warea-width {
          max-width: min(55rem, calc(100% - 2rem));
        }

        &.no-modal .warea {
          ${tw`md:px-2e`}
          ${isMd
            ? `
                max-width: ${WizardAreaWidthRem}rem;
                `
            : ''}
        }

        ${/* Centering content if container is taller but offset up 10px */ ''}
        &::before {
          content: '';
          flex-grow: 1;
          flex-basis: 0;
        }

        ${/* Centering content if container is taller but offset up 10px */ ''}
        &::after {
          content: '';
          flex-grow: 1;
          flex-basis: 5%;
        }
      `}
    >
      <div className='warea flex flex-col items-start justify-start w-full mx-auto' css={css``}>
        <div
          className={`warea-width ${inModal ? 'in-modal' : 'no-modal'} ${className}`}
          css={[
            tw`flex flex-col mx-auto items-center w-full text-lg text-color-input-field relative`,
            css`
              justify-content: flex-start;
              padding: 20px;
              @media (max-width: 767px) {
                padding-left: 1rem;
                padding-right: 1rem;
              }

              a {
                cursor: pointer;
              }
              a:hover {
                text-decoration: underline;
              }
            `,
          ]}
        >
          {children}
        </div>
      </div>
      {after}
    </div>
  );
});

const Subtitle: React.FC<BasicProps> = ({ className, children }) => {
  return (
    <div
      className={`wsubtitle ${className}`}
      css={[
        tw`font-heading font-semibold leading-none text-xl`,
        css`
          margin: 1rem 0 0;
          @media (min-width: 768px) {
            margin: calc(1.25rem - 0.14285714em) 0 0.25rem;
          }
          line-height: 2rem;

          &:first-of-type {
            margin-top: 0;
          }

          & + * {
            margin-top: 1rem;
          }
        `,
      ]}
    >
      {children}
    </div>
  );
};

const Container: React.FC<BasicProps & { render: boolean | undefined }> = ({
  children,
  render,
  className,
}) => {
  return (
    <div className={`wcontainer flex flex-col w-full justify-center items-center ${className}`}>
      {render ? children : null}
    </div>
  );
};

const Title: React.FC<BasicProps> = ({ className, children }) => {
  return (
    <div
      className={`wtitle ${className}`}
      css={[
        tw`font-heading font-semibold leading-tight text-coral-1 text-2xl md:text-4xl text-center`,
        css`
          @media (min-width: 768px) {
            margin: calc(2rem - 0.14285714em) 0 0;
          }
          & + * {
            ${tw`mt-1_5e md:mt-3e`}
          }
          & + .wtext {
            margin-top: 0.5rem;
          }
          & + ${Form.Field} {
            margin-top: 1rem;
          }
        `,
      ]}
    >
      {children}
    </div>
  );
};

const Text: React.FC<BasicProps> = ({ children, className }) => {
  return (
    <div
      className={`wtext ${className}`}
      css={[tw`text-gray-0 font-body leading-normal my-1_5e text-sm md:text-lg`]}
    >
      {children}
    </div>
  );
};

const NextButtonInner: React.FC<SubmitButtonProps> = React.memo((props) => {
  return (
    <Button.submit
      type='submit'
      css={[
        tw`py-2/3e w-full font-semibold text-lg md:max-w-1/2 mb-2e`,
        css`
          min-width: 13rem;
        `,
      ]}
      {...props}
    >
      {props.children ?? (
        <div className='flex items-center h-1_5e'>
          Next <NextIcon className='ml-3/4e h-1_25e' />
        </div>
      )}
    </Button.submit>
  );
});

const NextButton: React.FC<
  { type?: 'button' | 'submit'; disabled?: boolean; onClick?: () => void } & BasicProps
> = ({ className, onClick, disabled, children }) => {
  const { isSubmitting, onClickNext, activeChild, activeStep } = useWizard();
  const etheme = useThemeContext();
  const formHandlers = useFormContext();
  const type = activeChild.props.next === 'submit' ? 'submit' : 'button';
  const hideBack = activeChild.props.back === 'no' || activeStep === 0;

  const onNextHandler = React.useCallback(() => {
    if (type === 'submit') return;
    if (onClick) {
      onClick();
      return;
    }

    onClickNext();
  }, [onClickNext, type, onClick]);

  const onNext = useRefCallback(onNextHandler);

  return (
    <NextButtonInner
      disabled={disabled}
      className={`wnext ${className} ${hideBack ? 'hideback' : ''} ${className}`}
      submitting={isSubmitting}
      onClick={onNext}
      theme={
        formHandlers.formState.isValid
          ? etheme.controls.button.action
          : etheme.controls.button.invalid
      }
    >
      {children}
    </NextButtonInner>
  );
};

const BackButton: React.FC<BasicProps & { onClick?: () => Promise<void> }> = ({
  className,
  onClick,
  children,
}) => {
  const { previousStep, activeChild, activeStep } = useWizard();

  const onClickBack = React.useCallback(async () => {
    if (onClick) {
      await onClick();
      return;
    }

    previousStep();
  }, [onClick, previousStep]);

  const hideBack = activeChild.props.back === 'no' || activeStep === 0;
  if (hideBack) {
    return <div />;
  }

  return (
    <Button.rounded
      className={`wback ${className}`}
      css={css`
        ${tw`text-lg md:text-xl text-gray-0 mb-2e`}
      `}
      onClick={onClickBack}
      tabIndex={0}
      theme={{
        bg: 'transparent',
        bgHover: tw`text-gray-6`.toString(),
        fg: tw`text-gray-1`.toString(),
        fgHover: tw`text-gray-0`.toString(),
      }}
    >
      {children ?? <PrevIcon className='mr-3/4e h-1_25e' />}
    </Button.rounded>
  );
};

const ErrorArea: React.FC<BasicProps> = ({ className }) => {
  const formState = useFormContext();
  const defaultError = formState.formState.errors._?.message;

  if (!defaultError) return <div className={className} />;

  return <Form.ErrorMessage className={`flex items-center my-2e ${className}`} />;
};

const ButtonRow: React.FC<ButtonRowProps> = ({
  children,
  rowClassName,
  errorClassName,
  backButtonProps,
  ...props
}) => {
  return (
    <>
      <ErrorArea className={errorClassName ?? 'h-3e'} />
      <div
        className={rowClassName}
        css={css`
          ${tw`flex w-full justify-between items-baseline`}
        `}
      >
        <BackButton {...backButtonProps} />
        <NextButton {...props}>{children}</NextButton>
      </div>
    </>
  );
};

const Link: React.FC<BasicProps> = ({ children, className }) => {
  return (
    <div
      className={`wlink ${className}`}
      css={tw`text-gray-0 mt-3e mr-auto text-gray-0 leading-none whitespace-nowrap`}
    >
      {children}
    </div>
  );
};

const Separator: React.FC<BasicProps> = ({ children, className }) => {
  // Separator in the middle with a margin around {children}
  return (
    <div
      className={`wseparator flex items-center justify-center w-full ${className}`}
      css={css`
        &::before,
        &::after {
          content: '';
          border-top: 1px solid rgb(203, 214, 226);
          margin: 0px 15px 0px 0px;
          flex: 1 0 20px;
        }
        &::after {
          margin: 0px 0px 0px 15px;
        }
        margin: 1rem 0;
      `}
    >
      {children}
    </div>
  );
};

const Wizard: WizardComponents = {
  Area,
  Container,
  Title,
  Subtitle,
  Text,
  Link,
  ProgressBar,
  Separator,
  BackButton,
  NextButton,
  ErrorArea,
  ButtonRow,
};

export default Wizard;
